import { deviceStatusSnapshot } from './device-status-model'

function streamError (message, code) {
  const error = new Error(message)
  error.code = code
  return error
}

// 服务端推完整聚合快照，不拉设备明细，也不对每台设备分别订阅。
export function createDeviceStatusFeed ({ stream, snapshot, changed, status, validate = deviceStatusSnapshot, later = setTimeout, cancel = clearTimeout }) {
  let closed = false, subscription, timeout, loading = false, rerun = false, epoch = 0
  let initialized = false, streamReady = false, revision = 0, streamFault = null, httpFault = null
  const unsubscribe = () => {
    if (subscription) {
      const value = subscription; subscription = null
      try { value.unsubscribe() } catch (_) { /* 连接可能已关闭。 */ }
    }
  }
  const report = () => {
    if (closed) return
    const error = streamFault || (!initialized && httpFault)
    status(error ? 'error' : streamReady ? 'ready' : initialized ? 'connecting' : 'loading', error && error.message, error || undefined)
  }
  const denied = error => [401, 403].includes(Number(error && error.status))
  const stopDenied = error => {
    if (closed) return
    closed = true; cancel(timeout); unsubscribe()
    changed(null, { source: 'permission' })
    status('error', error.message || '没有设备统计访问权限', error)
  }
  const streamFailed = cause => {
    if (closed || streamFault) return
    const error = cause || streamError('设备在线状态订阅失败，请重试', 'DEVICE_STATUS_STREAM_FAILED')
    if (denied(error)) { stopDenied(error); return }
    streamFault = error; streamReady = false; cancel(timeout); unsubscribe()
    // 慢 HTTP 仍可提供初始统计，不因 WS 先失败就反复重建并取消查询。
    if (initialized || httpFault) report()
  }
  const refresh = async () => {
    if (closed) return
    if (loading) { rerun = true; return }
    loading = true
    const expectedEpoch = epoch
    if (!initialized) status('loading')
    try {
      const frame = validate(await snapshot())
      if (closed) return
      if (expectedEpoch !== epoch) { rerun = true; return }
      httpFault = null
      // HTTP 与 WS 不共用时序编号；一旦收到本轮 WS 快照，迟到 HTTP 绝不能倒灌。
      if (revision === 0) { initialized = true; changed(frame, { source: 'http' }) }
      report()
    } catch (error) {
      if (closed) return
      if (expectedEpoch !== epoch) { rerun = true; return }
      if (denied(error)) { stopDenied(error); return }
      httpFault = error
      // 初始 HTTP 失败不结束 WS；有效首帧仍可恢复，并清掉这次错误。
      report()
    } finally {
      loading = false
      if (rerun && !closed) { rerun = false; refresh() }
    }
  }
  const receive = response => {
    if (closed || streamFault) return
    try {
      const frame = validate(response && response.payload)
      if (frame.revision < 1) throw streamError('设备在线状态订阅版本无效', 'DEVICE_STATUS_REVISION_INVALID')
      if (frame.revision <= revision) return
      revision = frame.revision; initialized = true; streamReady = true; httpFault = null
      // 服务端每10秒按最新授权校准并发送存活快照；首帧后也要监测静默失联。
      watchTimeout(35000)
      changed(frame, { source: 'realtime' }); report()
    } catch (error) { streamFailed(error) }
  }
  const watchTimeout = (delay = 15000) => {
    cancel(timeout)
    timeout = later(() => streamFailed(streamError('设备在线状态订阅超时，请重试', 'DEVICE_STATUS_STREAM_TIMEOUT')), delay)
  }
  status('loading')
  watchTimeout()
  try {
    if (stream.onReconnect) stream.onReconnect(() => {
      if (closed) return
      epoch++; initialized = false; streamReady = false; streamFault = null; httpFault = null; revision = 0
      changed(null, { source: 'reconnect' }); status('loading'); watchTimeout(); refresh()
    })
    subscription = stream.subscribe(receive, streamFailed,
      () => streamFailed(streamError('设备在线状态订阅已结束，请重试', 'DEVICE_STATUS_STREAM_ENDED')))
    if (closed || streamFault) unsubscribe()
  } catch (error) { streamFailed(error) }
  refresh()
  return { close () {
    closed = true; cancel(timeout); unsubscribe()
  } }
}
